import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import { treeService } from '../services/treeService';
import { EditorNode, EditorTree } from '../types/types';
import { treeUtils } from '../services/treeUtils';
import { OrganizationDirection, OrganizationMode } from '../types/organization';

interface TreeState {
  // Core tree state
  currentTree: EditorTree | null;
  selectedNode: EditorNode | null;
  selectedEdge: {
    sourceNodeId: string | null;
    targetNodeId: string | null;
  };
  selectedNodeId: string | null;
  loading: boolean;
  error: string | null;
  expandedNodeIds: string[];
  
  // Tree organization settings
  organizationMode: OrganizationMode;
  organizationDirection: OrganizationDirection;
  visualizationDimensions: { width: number; height: number };

  templateNode: (parentId?: string) => EditorNode;

  // Basic Actions
  setCurrentTree: (tree: EditorTree | null) => void;
  setSelectedNode: (nodeId: string | null) => void;

  setSelectedEdge: (sourceNodeId: string | null, targetNodeId: string | null) => void;
  setExpandedNodes: (nodeIds: string[]) => void;
    // Tree operations
  loadTree: (id: string) => Promise<void>;
  loadTreeLegacy: (id: string) => Promise<any>;
  saveTree: () => void; // Save to localStorage (automatic via middleware)
  saveToBackend: () => Promise<void>; // Save to backend API
  createTree: (title: string, description: string) => void;
  resetTree: () => void;
  clearCurrentTree: () => void;
  
  // Node operations
  addNode: (node: EditorNode, parentId?: string) => void;
  updateNode: (updatedNode: EditorNode) => void;
  updateNodePosition: (nodeId: string, x: number, y: number) => void;
  deleteNode: (nodeId: string) => void;
  
  // Tree calculation
  calculateLocalTree: () => void; // Basic calculation
  
  // Tree organization
  setOrganizationMode: (value: OrganizationMode) => void;
  setOrganizationDirection: (direction: OrganizationDirection) => void;
  organizeTree: () => void; // Manual organization trigger

  setVisualizationDimensions: (width: number, height: number) => void;
  
  // Chance node operations
  updateNodeProbability: (nodeId: string, probability: number, probabilityEdited?: boolean) => void;
  updateNodeValue: (nodeId: string, value: number) => void;
  updateNodeLabel: (nodeId: string, name: string) => void;
  distributeNodeProbabilities: (parentNodeId: string) => void;
  
  // Import/Export
  exportTreeJson: () => void;
  exportTreePng: () => void;
  exportTreeSvg: () => void;
  exportTreePDF: () => void;
  importTreeJson: (file: File) => Promise<void>;
}

/**
 * Store for managing the current tree being edited
 * 
 * This store is focused on:
 * 1. Managing the state of the tree currently being edited
 * 2. Operations on the current tree (nodes, calculations, etc.)
 * 3. Persisting changes to localStorage automatically
 * 4. Saving to backend when explicitly requested
 */
export const useTreeStore = create<TreeState>()(
  persist(
    immer((set, get) => ({
      // Core tree state
      currentTree: null,
      selectedNode: null,
      selectedNodeId: null,
      selectedEdge: {
        sourceNodeId: null,
        targetNodeId: null
      },
      loading: false,
      error: null,
      expandedNodeIds: [],
      
      // Tree organization settings
      organizationMode: 'auto',
      organizationDirection: OrganizationDirection.VERTICAL,
      visualizationDimensions: { width: 800, height: 600 },

      templateNode: (parentId?: string) => {
        const nodeId = treeUtils.generateNodeId(get().currentTree, parentId);
        return {
          id: nodeId,
          name: `Node ${nodeId}`,
          type: 'decision',
          decision_strategy: 'max',
          value: 0,
          x: get().visualizationDimensions.width / 2, //TODO add offset based on parent if present
          y: get().visualizationDimensions.height / 2,
          parent_id: parentId,
          children: [],
          level: treeUtils.getNodeLevel(get().currentTree, parentId),
          edge: {
            name: undefined,
          },
        };
      },

      // Basic setters
      setCurrentTree: (tree) => {
        set({ currentTree: tree });
      },
      
      setSelectedNode: (nodeId) => set({ selectedNodeId: nodeId }),

      setSelectedEdge: (sourceNodeId, targetNodeId) => {
        set({ selectedEdge: { sourceNodeId, targetNodeId } });  
      },
      
      setExpandedNodes: (nodeIds) => set({ expandedNodeIds: nodeIds }),

      // Tree operations
      loadTree: async (id) => {
        set({ loading: true, error: null });
        try {
          const tree = await treeService.getTree(id);
          
          set(state => {
            state.currentTree = tree;
            state.loading = false;
            
            // Auto-expand all nodes for initial display
            if (tree && tree.nodes) {
              state.expandedNodeIds = tree.nodes.map(node => node.id);
            }

            // Auto organize if enabled
            if (state.organizationMode && state.currentTree) {
              const dims = { width: 800, height: 600 }; // Default dimensions, should be updated from UI
              state.currentTree = treeUtils.organizeTreeLayout(state.currentTree, state.organizationDirection, state.organizationMode, dims);
            }
          });
        } catch (error: any) {
          console.error('Failed to load tree:', error);
          set({ error: `Failed to load tree: ${error.message || error}`, loading: false });
        }
      },

      loadTreeLegacy: async (id) => {
        set({ loading: true, error: null });
        try {
          const tree = await treeService.getTree(id);
          
          return tree;
        } catch (error: any) {
          console.error('Failed to load tree:', error);
          set({ error: `Failed to load tree: ${error.message || error}`, loading: false });
        }
      },

      saveTree: () => {
        // No explicit action needed as state is automatically persisted to localStorage
        // Just update the updatedAt timestamp
        const { currentTree } = get();
        
        if (currentTree) {
          set(state => {
            if (state.currentTree) {
              state.currentTree.updatedAt = new Date().toISOString();
            }
          });
        }
      },
      
      saveToBackend: async () => {
        const { currentTree } = get();
        if (!currentTree) return;
  
        set({ loading: true, error: null });
        try {
          const savedTree = await treeService.saveTreeToBackend(currentTree);
          set({ currentTree: savedTree, loading: false });
        } catch (error: any) {
          console.error('Failed to save tree to backend:', error);
          set({ error: `Failed to save tree: ${error.message || error}`, loading: false });
        }
      },

      createTree: (title, description) => {
        const newTree = treeService.createLocalTree(title, description);
        set({ currentTree: newTree, loading: false });
      },
      
      clearCurrentTree: () => {
        set({ currentTree: null, selectedNodeId: null, expandedNodeIds: [] });
      },

      resetTree: () => {
        set({ currentTree: null, selectedNodeId: null, expandedNodeIds: [] });
      },

      // Node operations
      addNode: (node, parentId) => {
        set(state => {
          // Ensure current tree exists and has a nodes array
          if (!state.currentTree) {
            state.currentTree = {
              id: 'new',
              filename: 'new_workspace',
              title: "New Decision Tree",
              description: "A new decision tree",
              nodes: [],
              total_expected_value: 0,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
          }
          
          if (!state.currentTree.nodes) {
            state.currentTree.nodes = [];
          }

          // Add node to the tree
          state.currentTree.nodes.push(node);

          // Update parent's children array
          if (parentId) {
            const parentNode = state.currentTree.nodes.find(n => n.id === parentId);
            if (parentNode) {
              if (!parentNode.children) parentNode.children = [];
              
              // Make sure the child isn't already in the list
              if (!parentNode.children.includes(node.id)) {
                parentNode.children.push(node.id);
              }
            }
          }
          
          // Add to expanded nodes
          if (!state.expandedNodeIds.includes(node.id)) {
            state.expandedNodeIds.push(node.id);
          }
          
          // If node has a parent, make sure parent is expanded
          if (node.parent_id && !state.expandedNodeIds.includes(node.parent_id)) {
            state.expandedNodeIds.push(node.parent_id);
          }

          // Auto organize if enabled
          if (state.organizationMode && state.currentTree) {
            const dims = { width: 800, height: 600 }; // Default dimensions, should be updated from UI
            state.currentTree = treeUtils.organizeTreeLayout(state.currentTree, state.organizationDirection, state.organizationMode, dims);
          }
          
          // Update tree's updatedAt timestamp
          state.currentTree.updatedAt = new Date().toISOString();
        });
        
          // Run local calculation after adding node
        get().calculateLocalTree();
      },

      updateNode: (updatedNode) => {
        set(state => {
          if (!state.currentTree) return;
          
          const index = state.currentTree.nodes.findIndex(n => n.id === updatedNode.id);
          if (index >= 0) {
            state.currentTree.nodes[index] = updatedNode;
            state.currentTree.updatedAt = new Date().toISOString();
          }
        });
        // Run local calculation after updating node
        get().calculateLocalTree();
      },

      updateNodePosition: (nodeId, x, y) => {
        set(state => {
          if (!state.currentTree) return;
          
          const node = state.currentTree.nodes.find(n => n.id === nodeId);
          if (node) {
            node.x = x;
            node.y = y;
            state.currentTree.updatedAt = new Date().toISOString();
          }
        });
      },

      deleteNode: (nodeId) => {
        set(state => {
          if (!state.currentTree) return;
          
          // Find the node
          const nodeToDelete = state.currentTree.nodes.find(n => n.id === nodeId);
          if (!nodeToDelete) return;
          
          // Update parent's children array
          if (nodeToDelete.parent_id) {
            const parentNode = state.currentTree.nodes.find(n => n.id === nodeToDelete.parent_id);
            if (parentNode && parentNode.children) {
              parentNode.children = parentNode.children.filter(id => id !== nodeId);
            }
          }
          
          // Remove from expanded nodes
          state.expandedNodeIds = state.expandedNodeIds.filter(id => id !== nodeId);
          
          // If this was the selected node, clear selection
          if (state.selectedNodeId === nodeId) {
            state.selectedNodeId = null;
          }
          
          // Remove this node and all its descendants
          const nodesToRemove = [nodeId];
          
          // Recursively find all descendant nodes
          const findDescendants = (parentId: string) => {
            const children = state.currentTree!.nodes
              .filter(n => n.parent_id === parentId)
              .map(n => n.id);
            
            nodesToRemove.push(...children);
            children.forEach(childId => findDescendants(childId));
          };
          
          findDescendants(nodeId);
          
          // Filter out all nodes to remove
          state.currentTree.nodes = state.currentTree.nodes
            .filter(n => !nodesToRemove.includes(n.id));
          
          // Auto organize if enabled
          if (state.organizationMode && state.currentTree.nodes.length > 0) {
            const dims = { width: 800, height: 600 }; // Default dimensions, should be updated from UI
            state.currentTree = treeUtils.organizeTreeLayout(state.currentTree, state.organizationDirection, state.organizationMode, dims);
          }
          
          state.currentTree.updatedAt = new Date().toISOString();
        });

        // Run local calculation after deleting node
        get().calculateLocalTree();
      },

      // Tree calculation
      calculateLocalTree: () => {
        set(state => {
          if (!state.currentTree) return;
          
          // Simple calculation logic - sum up values from bottom to top
          const calculateNodeValue = (nodeId: string): number => {
            const node = state.currentTree!.nodes.find(n => n.id === nodeId);
            if (!node) return 0;
            
            // Calculate cumulative probability and payoff
            // If node has a parent, calculate cumulative values based on parent's values
            if (node.parent_id) {
              const parentNode = state.currentTree!.nodes.find(n => n.id === node.parent_id);

              // if parent node is a chance node, distribute probabilities based on children who haven't been manually edited
              if (parentNode && parentNode.type === 'chance') {
                const children = state.currentTree!.nodes.filter(n => n.parent_id === node.parent_id);
                const uneditedChildren = children.filter(child => !child.edge.probability_edited);
                const editedChildrenTotal = children
                  .filter(child => child.edge.probability_edited)
                  .map(child => child.edge.probability || 0)
                  .reduce((sum, val) => sum + val, 0);
                if (uneditedChildren.length > 0) {
                  const probability = (1 - editedChildrenTotal) / uneditedChildren.length;
                  uneditedChildren.forEach(child => {
                    child.edge.probability = probability;
                  });
                }
              }

              //then calculate the total probability
              if (
                parentNode &&
                parentNode.edge &&
                parentNode.edge.joint_probability !== undefined &&
                parentNode.edge.total_payoff !== undefined
              ) {
                node.edge.joint_probability = parentNode.edge.joint_probability * (node.edge.probability || 0);
                node.edge.total_payoff = parentNode.edge.total_payoff + (node.edge.payoff || 0);
              }
            }
            else {
              node.edge.joint_probability = 1;
              node.edge.total_payoff = 0;
            }

            if (node.type === 'terminal') {
              node.value = node.edge.total_payoff || 0;
              return node.edge.total_payoff || 0;
            }

            // If node has no children and isn't terminal just continue
            const childNodes = state.currentTree!.nodes.filter(n => n.parent_id === nodeId);
            if (!childNodes.length) return node.value || 0;

            if (node.type === 'chance') {
              // Weighted sum for chance nodes
              const weightedSum = childNodes.reduce((sum, child) => {
                const childValue = calculateNodeValue(child.id);
                const probability = child.edge.probability || 0;
                return sum + (childValue * probability);
              }, 0);
              
              node.value = weightedSum;
              return weightedSum;
            } 
            if (node.type === 'decision') {
              const childValues = childNodes.map(child => calculateNodeValue(child.id));
              let value = 0;

              switch (node.decision_strategy) {
              case 'max':
              case 'maximin':
              case 'maximax':
                value = Math.max(...childValues);
                break;
              case 'min':
              case 'minimin':
              case 'minimax':
                value = Math.min(...childValues);
                break;
              default:
                value = Math.max(...childValues);
              }

              // Filter the indexes of the optimal child
              const optimalIndexes = childValues
                .map((v, i) => (v === value ? i : -1))
                .filter(i => i !== -1);

              // Mark all children as not optimal and set probability to 0
              treeUtils.markSubTreeOptimal(state.currentTree, node, false);

              // Mark the optimal child and all it's children recursively as optimal
              optimalIndexes.forEach(idx => {
                if (childNodes[idx]) {
                  childNodes[idx].optimal_choice = true;
                  childNodes[idx].edge.probability = 1;
                  treeUtils.markSubTreeOptimal(state.currentTree, childNodes[idx], true);
                }
              });

              node.value = value;
              return value;
            }
            //default value (No children)
            return 0;
          };

          // Find root nodes and calculate from there
          const rootNodes = state.currentTree.nodes.filter(n => !n.parent_id);
          let totalValue = 0;
          
          rootNodes.forEach(root => {
            totalValue += calculateNodeValue(root.id);
          });
          
          state.currentTree.total_expected_value = totalValue;
          state.currentTree.updatedAt = new Date().toISOString();
        });
      },
      
      
      // Tree organization
      setOrganizationMode: (value: OrganizationMode) => {
        set({ organizationMode: value });

        // If turning auto-organize on, organize the tree immediately
        if (value !== 'manual' ) {
          get().organizeTree();
        }
      },
      
      setOrganizationDirection: (direction) => {
        set({ organizationDirection: direction });
        
        // If auto-organize is on, reorganize with new direction
        if (get().organizationMode !== 'manual' ) {
          get().organizeTree();
        }
      },
      
      organizeTree: () => {
        set(state => {
          if (!state.currentTree || !state.currentTree.nodes.length) return;

          const dims = get().visualizationDimensions; // Default dimensions, should be updated from UI
          state.currentTree = treeUtils.organizeTreeLayout(
            state.currentTree, 
            state.organizationDirection, 
            state.organizationMode,
            dims
          );
          
          state.currentTree.updatedAt = new Date().toISOString();
        });
      },

      setVisualizationDimensions: (width, height) => {
        set({ visualizationDimensions: { width, height } });
        // Reorganize tree if auto-organize is enabled
        if (get().organizationMode !== 'manual' ) {
          get().organizeTree();
        }
      },
      
      // Chance node operations
      updateNodeProbability: (nodeId, probability, probabilityEdited = false) => {
        set(state => {
          if (!state.currentTree) return;
          
          const node = state.currentTree.nodes.find(n => n.id === nodeId);
          if (node) {
            node.edge.probability = probability;
            node.edge.probability_edited = probabilityEdited;
            state.currentTree.updatedAt = new Date().toISOString();
          }
        });
        // Run local calculation after updating node
        get().calculateLocalTree();
      },
      
      updateNodeValue: (nodeId, value) => {
        set(state => {
          if (!state.currentTree) return;
          
          const node = state.currentTree.nodes.find(n => n.id === nodeId);
          if (node) {
            node.value = value;
            state.currentTree.updatedAt = new Date().toISOString();
          }
        });
        // Run local calculation after updating node
        get().calculateLocalTree();
      },

      updateNodeLabel: (nodeId, name) => {
        set(state => {
          if (!state.currentTree) return;
          
          const node = state.currentTree.nodes.find(n => n.id === nodeId);
          if (node) {
            node.name = name;
            state.currentTree.updatedAt = new Date().toISOString();
          }
        });
      },
      
      distributeNodeProbabilities: (parentNodeId) => {
        set(state => {
          if (!state.currentTree) return;
          if (!parentNodeId) return;
          
          // Find the parent node
          const parentNode = state.currentTree.nodes.find(n => n.id === parentNodeId);
          if (!parentNode || parentNode.type !== 'chance') return;
          
          // Find all children
          const children = state.currentTree.nodes.filter(n => n.parent_id === parentNodeId);
          if (!children.length) return;
          
          // Distribute probabilities evenly
          const probability = 1 / children.length;
          
          children.forEach(child => {
            child.edge.probability = probability;
            child.edge.probability_edited = false; // Reset edited flag
          });
          
          state.currentTree.updatedAt = new Date().toISOString();
          
          // Run local calculation after updating probabilities
          const stateSnapshot = get();
          stateSnapshot.calculateLocalTree();
        });
      },
      
      // Import/Export
      exportTreeJson: () => {
        const { currentTree } = get();
        if (!currentTree) return;

        treeService.exportTreeAsJson(currentTree);
      },
      exportTreePng: () => {
        const { currentTree } = get();
        if (!currentTree) return;

        treeService.exportTreeAsPng(currentTree);
      },
      exportTreeSvg: () => {
        const { currentTree } = get();
        if (!currentTree) return;

        treeService.exportTreeAsSvg(currentTree);
      },
      exportTreePDF: () => {
        const { currentTree } = get();
        if (!currentTree) return;

        treeService.exportTreeAsPDF(currentTree);
      },

      importTreeJson: async (file) => {
        set({ loading: true, error: null });
        try {
          const importedTree = await treeService.importTreeFromJson(file);
          set({ 
            currentTree: importedTree, 
            loading: false,
            expandedNodeIds: importedTree.nodes.map(node => node.id)
          });
        } catch (error: any) {
          console.error('Failed to import tree:', error);
          set({ error: `Failed to import tree: ${error.message || error}`, loading: false });
        }
      }
    })),
    {
      name: 'decision-tree-storage', // name of the localStorage item
      partialize: (state) => ({ 
        currentTree: state.currentTree,
        organizationDirection: state.organizationDirection,
        organizationMode: state.organizationMode,
      }), // only store these state properties
    }
  )
);